import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  PageManagement, 
  PageManagementCreateDto, 
  PageManagementUpdateDto,
  PageManagementResponse,
  QueryPageManagementDto
} from "../../modules/shared/model/page-management.interfaces";
import { 
  getPageManagements as getPageManagementsAction,
  getPageManagementById as getPageManagementByIdAction,
  createPageManagement as createPageManagementAction,
  updatePageManagement as updatePageManagementAction,
  deletePageManagement as deletePageManagementAction
} from "../Actions/PageManagementActions";

// Async Thunks
export const fetchPageManagements = createAsyncThunk("pageManagements/fetchAll", async (query: QueryPageManagementDto | undefined, { rejectWithValue }) => {
  try { 
    return await getPageManagementsAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch page managements"); 
  }
});

export const fetchPageManagementById = createAsyncThunk("pageManagements/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getPageManagementByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch page management"); 
  }
});


export const createPageManagement = createAsyncThunk("pageManagements/create", async (data: PageManagementCreateDto, { rejectWithValue }) => {
  try { 
    return await createPageManagementAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create page management"); 
  }
});

export const updatePageManagement = createAsyncThunk("pageManagements/update", async ({ id, data }: { id: string; data: PageManagementUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updatePageManagementAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update page management"); 
  }
});

export const deletePageManagement = createAsyncThunk("pageManagements/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deletePageManagementAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete page management"); 
  }
});

// State interface
interface PageManagementState {
  // All page managements (paginated)
  pageManagements: PageManagement[];
  currentPageManagement: PageManagement | null;
  pageManagementsLoading: boolean;
  pageManagementsError: string | null;
  pageManagementsTotalCount?: number;
  pageManagementsCurrentPage: number;
  pageManagementsHasNextPage: boolean;
  pageManagementsHasPreviousPage: boolean;
  pageManagementsSearchQuery: string;
  
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
}

const initialState: PageManagementState = {
  // All page managements
  pageManagements: [],
  currentPageManagement: null,
  pageManagementsLoading: false,
  pageManagementsError: null,
  pageManagementsTotalCount: 0,
  pageManagementsCurrentPage: 1,
  pageManagementsHasNextPage: false,
  pageManagementsHasPreviousPage: false,
  pageManagementsSearchQuery: "",
  
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
};

const pageManagementSlice = createSlice({
  name: "pageManagements",
  initialState,
  reducers: {
    clearPageManagementsError(state) { 
      state.pageManagementsError = null; 
    },
    clearCurrentPageManagement(state) { 
      state.currentPageManagement = null; 
    },
    setPageManagementsSearchQuery(state, action: PayloadAction<string>) { 
      state.pageManagementsSearchQuery = action.payload; 
    },
    setPageManagementFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
    clearAllPageManagements(state) {
      state.pageManagements = [];
      state.currentPageManagement = null;
      state.pageManagementsTotalCount = 0;
    },
    updatePageManagementFromForm(state, action: PayloadAction<Partial<PageManagement>>) {
      if (state.currentPageManagement) {
        state.currentPageManagement = { ...state.currentPageManagement, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    // All Page Managements Extra Reducers
    builder
      .addCase(fetchPageManagements.pending, (state) => { 
        state.pageManagementsLoading = true; 
        state.pageManagementsError = null; 
      })
      .addCase(fetchPageManagements.fulfilled, (state, action: PayloadAction<PageManagementResponse>) => { 
        state.pageManagementsLoading = false; 
        state.pageManagements = action.payload.data || []; 
        state.pageManagementsTotalCount = action.payload.totalCount || 0; 
        state.pageManagementsCurrentPage = action.payload.page || 1;
        state.pageManagementsHasNextPage = action.payload.hasNextPage || false;
        state.pageManagementsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchPageManagements.rejected, (state, action: any) => { 
        state.pageManagementsLoading = false; 
        state.pageManagementsError = action.payload as string; 
      });

    builder
      .addCase(fetchPageManagementById.pending, (state) => { 
        state.pageManagementsLoading = true; 
        state.pageManagementsError = null; 
      })
      .addCase(fetchPageManagementById.fulfilled, (state, action: PayloadAction<PageManagement>) => { 
        state.pageManagementsLoading = false; 
        state.currentPageManagement = action.payload; 
      })
      .addCase(fetchPageManagementById.rejected, (state, action: any) => { 
        state.pageManagementsLoading = false; 
        state.pageManagementsError = action.payload as string; 
      });

    // Create Extra Reducers
    builder
      .addCase(createPageManagement.pending, (state) => { 
        state.pageManagementsLoading = true; 
        state.pageManagementsError = null; 
      })
      .addCase(createPageManagement.fulfilled, (state, action: PayloadAction<PageManagement>) => { 
        state.pageManagementsLoading = false; 
        state.pageManagements.unshift(action.payload); 
        state.pageManagementsTotalCount = (state.pageManagementsTotalCount || 0) + 1; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createPageManagement.rejected, (state, action: any) => { 
        state.pageManagementsLoading = false; 
        state.pageManagementsError = action.payload as string; 
      });

    // Update Extra Reducers
    builder
      .addCase(updatePageManagement.pending, (state) => { 
        state.pageManagementsLoading = true; 
        state.pageManagementsError = null; 
      })
      .addCase(updatePageManagement.fulfilled, (state, action: PayloadAction<PageManagement>) => {
        state.pageManagementsLoading = false;
        const index = state.pageManagements.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.pageManagements[index] = action.payload;
        if (state.currentPageManagement?.id === action.payload.id) state.currentPageManagement = action.payload;
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updatePageManagement.rejected, (state, action: any) => { 
        state.pageManagementsLoading = false; 
        state.pageManagementsError = action.payload as string; 
      });

    // Delete Extra Reducers
    builder
      .addCase(deletePageManagement.pending, (state) => { 
        state.pageManagementsLoading = true; 
        state.pageManagementsError = null; 
      })
      .addCase(deletePageManagement.fulfilled, (state, action: PayloadAction<string>) => { 
        state.pageManagementsLoading = false; 
        state.pageManagements = state.pageManagements.filter(x => x.id !== action.payload); 
        if (state.currentPageManagement?.id === action.payload) state.currentPageManagement = null; 
        state.pageManagementsTotalCount = Math.max(0, (state.pageManagementsTotalCount || 0) - 1); 
      })
      .addCase(deletePageManagement.rejected, (state, action: any) => { 
        state.pageManagementsLoading = false; 
        state.pageManagementsError = action.payload as string; 
      });
  }
});

export const { 
  clearPageManagementsError,
  clearCurrentPageManagement, 
  setPageManagementsSearchQuery,
  setPageManagementFormDirty,
  clearAllPageManagements,
  updatePageManagementFromForm
} = pageManagementSlice.actions;

export default pageManagementSlice.reducer;



